import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Col,
  Drawer,
  Row,
  Spin,
  Tabs,
} from 'antd';

import FormBuilder from '../FormBuilder/FormBuilder';
import FormWorkflows from '../FormWorkflows/FormWorkflows';
import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import Permissions from '../../auth/Permissions';
import {
  getTemplateVersionById,
  restoreFormTemplateVersion,
} from '../state/forms.actions';

const { TabPane } = Tabs;

const noop = () => {};

function FormTemplateVersionPreviewDrawer({
  open,
  onClose,
  templateId,
  versionId,
  versionNumber,
  isLatest,
  isLegacy,
  initialTab,
  onRestoreComplete,
}) {
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const template = useSelector((state) => state.forms.templates[templateId]);
  const formTypes = useSelector((state) => state.forms.types);
  const version = useSelector((state) => state.forms.templateVersionMap[templateId]?.[versionId]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [prevOpen, setPrevOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const {
    name: templateName,
    divisionId,
    formData = {},
    isExternalForm = false,
    type,
    typeId,
  } = template ?? {};

  const drawerTitle = isLegacy
    ? 'Changes before versioning (Version 1)'
    : `Version ${versionNumber} Preview`;
  const canRestore = Permissions.has('FORMS_WRITE') && !isLatest;

  const snapshot = version?.snapshot ?? {};
  const sections = snapshot.sections ?? [];
  const workflow = snapshot.workflow ?? {};
  const closePOWorkflow = snapshot.closePOWorkflow ?? { nodes: [], edges: [] };
  const isPO = type === 'PO'
    || formTypes.find?.((formType) => formType.id === typeId)?.name === 'PO';

  if (open !== prevOpen) {
    setActiveTab(initialTab ?? 'fields');
    setPrevOpen(open);
  }

  useEffect(() => {
    const loadVersion = async () => {
      if (!open || !templateId || !versionId || version?.dataLoaded) return;
      setLoading(true);
      await dispatch(getTemplateVersionById(templateId, versionId));
      setLoading(false);
    };
    loadVersion();
  }, [open, templateId, versionId, version?.dataLoaded]);

  const onExit = useCallback(() => {
    onClose();
  }, [onClose]);

  const onRestore = useCallback(async () => {
    CustomConfirmModal({
      title: `Restore Version ${versionNumber}`,
      content: 'Are you sure you want to restore this version? All fields and workflows will be replaced with the version you are restoring.',
      okText: 'Restore',
      onOk: async () => {
        setRestoring(true);
        const success = await dispatch(restoreFormTemplateVersion(templateId, versionId));
        setRestoring(false);
        if (success) {
          onRestoreComplete?.();
          onClose();
        }
      },
    });
  }, [templateId, versionId, versionNumber, onRestoreComplete, onClose]);

  return (
    <Drawer
      visible={open}
      onClose={onExit}
      title={drawerTitle}
      width="calc(60% - 25px)"
      placement="right"
    >
      <div style={{ position: 'relative', minHeight: '100%' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ padding: '0 24px' }}
        >
          <TabPane tab="Fields" key="fields">
            <FormBuilder
              templateId={templateId}
              name={templateName}
              divisions={divisionId ? [divisionId] : []}
              isDisplay
              initialSections={sections}
              isDetailView
            />
          </TabPane>
          <TabPane tab="Workflow" key="workflow">
            <FormWorkflows
              name={templateName}
              templateId={templateId}
              formRef={formRef}
              isDisplay
              isAdd={false}
              collected={formData.collected ?? false}
              sections={sections}
              exportLocation={formData.exportLocation}
              onWorkflowChange={noop}
              workflow={workflow}
              currentStep={2}
              divisionId={divisionId}
              drawOptions={formData.drawOptions ?? []}
              fileMap={{}}
              currentFormData={formData}
              isExternalForm={isExternalForm}
              setDisableNext={noop}
              isV3Form={template?.pdfVersion === 3}
            />
          </TabPane>
          {isPO && (
            <TabPane tab="Close PO Workflow" key="closePOWorkflow">
              <FormWorkflows
                name={templateName}
                templateId={templateId}
                formRef={formRef}
                isDisplay
                isAdd={false}
                collected={formData.collected ?? false}
                sections={sections}
                exportLocation={formData.exportLocation}
                onWorkflowChange={noop}
                workflow={closePOWorkflow}
                currentStep={3}
                divisionId={divisionId}
                drawOptions={formData.drawOptions ?? []}
                fileMap={{}}
                currentFormData={formData}
                isExternalForm={isExternalForm}
                setDisableNext={noop}
                isV3Form={template?.pdfVersion === 3}
                isClosePOWorkflow
              />
            </TabPane>
          )}
        </Tabs>
        {(loading || restoring) && (
          <Row
            style={{ position: 'absolute', inset: 0 }}
            justify="center"
            align="middle"
          >
            <Col>
              <Spin size="large" />
            </Col>
          </Row>
        )}
      </div>
      <DrawerSubmitFooter
        onClose={onExit}
        submitTitle="Restore"
        closeTitle="Exit Preview"
        onSubmit={canRestore ? onRestore : null}
        canSubmit={canRestore}
        loading={restoring}
      />
    </Drawer>
  );
}

FormTemplateVersionPreviewDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templateId: PropTypes.string,
  versionId: PropTypes.string,
  versionNumber: PropTypes.number,
  isLatest: PropTypes.bool,
  isLegacy: PropTypes.bool,
  initialTab: PropTypes.string,
  onRestoreComplete: PropTypes.func,
};

FormTemplateVersionPreviewDrawer.defaultProps = {
  templateId: null,
  versionId: null,
  versionNumber: null,
  isLatest: false,
  isLegacy: false,
  initialTab: 'fields',
  onRestoreComplete: null,
};

export default FormTemplateVersionPreviewDrawer;
